import events from "@mongez/events";
import { Cart, cartAtom } from "core/cart";
import { Product, productsListAtom, ShopProduct } from "core/catalog";
import { checkoutAtom } from "core/checkout";
import { useCallback, useEffect, useRef } from "react";
import { CartEvents } from "~/modules/cart/events";

type CartUpdateEventData = {
  cart: Cart;
  product: Product | ShopProduct;
};

type CartRemoveEventData = {
  product: Product | ShopProduct;
  cart: Cart;
};

/**
 * Updates the product's cartItem reference in the products list
 */
function updateProductCartItem(productId: number, cartItem: any) {
  const products = productsListAtom.get("products");

  if (!products?.length) return;

  productsListAtom.change(
    "products",
    products.map((product) =>
      product.id === productId ? { ...product, cartItem } : product,
    ),
  );
}

/**
 * Removes the cartItem reference from a specific product in the products list
 */
function removeProductCartItem(productId: number) {
  const products = productsListAtom.get("products");

  if (!products?.length) return;

  productsListAtom.change(
    "products",
    products.map((product) => {
      if (product.id === productId) {
        const { cartItem, ...productWithoutCartItem } = product;
        return productWithoutCartItem;
      }
      return product;
    }),
  );
}

/**
 * Removes cartItem references from all products in the products list
 */
function clearAllProductCartItems() {
  const products = productsListAtom.get("products");

  if (!products?.length) return;

  productsListAtom.change(
    "products",
    products.map((product) => {
      const { cartItem, ...productWithoutCartItem } = product;
      return productWithoutCartItem;
    }),
  );
}

/**
 * Hook to subscribe to cart events and automatically sync cart state with products list
 *
 * This hook listens to cart events and updates:
 * - The cartAtom with the latest cart data
 * - The productsListAtom with updated cartItem references for each product
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useCartEvents();
 *   // Now this component will react to cart events
 *   return <div>...</div>;
 * }
 * ```
 */
export function useCartEvents() {
  const isSubscribedRef = useRef(false);

  /**
   * Handles cart item updated events
   * Updates both cart state and the product's cartItem reference
   */
  const handleCartUpdated = useCallback((data: CartUpdateEventData) => {
    try {
      // Update cart atom
      cartAtom.change("cart", data.cart);

      // Find the cart item for this product
      const cartItem =
        data.cart.items.find((item) => item.product.id === data.product.id) ??
        null;

      // Update the product's cartItem reference
      updateProductCartItem(data.product.id, cartItem);
    } catch (error) {
      console.error("Error handling cart updated event:", error);
    }
  }, []);

  /**
   * Handles cart item removed events
   * Updates cart state and removes the product's cartItem reference
   */
  const handleCartRemoved = useCallback((data: CartRemoveEventData) => {
    try {
      // Update cart atom
      cartAtom.change("cart", data.cart);

      // Remove the product's cartItem reference
      removeProductCartItem(data.product.id);
    } catch (error) {
      console.error("Error handling cart removed event:", error);
    }
  }, []);

  /**
   * Handles cart cleared events
   * Resets cart and clears all product cartItem references
   */
  const handleCartCleared = useCallback(() => {
    try {
      // Reset cart atom
      cartAtom.reset();
      checkoutAtom.reset();

      // Clear all product cartItem references
      clearAllProductCartItems();
    } catch (error) {
      console.error("Error handling cart cleared event:", error);
    }
  }, []);

  useEffect(() => {
    // Prevent double subscription if hook is used multiple times
    if (isSubscribedRef.current) return;

    isSubscribedRef.current = true;

    /**
     * Subscribe to cart events
     */
    const unsubscribeCartUpdated = events.subscribe(
      CartEvents.CART_ITEM_UPDATED,
      handleCartUpdated,
    );

    const unsubscribeCartRemoved = events.subscribe(
      CartEvents.CART_ITEM_REMOVED,
      handleCartRemoved,
    );

    const unsubscribeCartCleared = events.subscribe(
      CartEvents.CART_CLEARED,
      handleCartCleared,
    );

    // Cleanup function to unsubscribe from all events
    return () => {
      unsubscribeCartUpdated.unsubscribe();
      unsubscribeCartRemoved.unsubscribe();
      unsubscribeCartCleared.unsubscribe();
      isSubscribedRef.current = false;
    };
  }, [handleCartUpdated, handleCartRemoved, handleCartCleared]);
}
